import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import ExportCsvPrime from '../../global/ExportCsvPrime';
import { Menu } from 'primereact/menu';
import { useTranslation } from 'react-i18next';
import func from '../../../services/functions';
import moment from 'moment/moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import TooltipOverlay from '../../global/TooltipOverlay';

const BankJournalExportMenu = ({
  data,
  columns,
  selectedColumns,
  excludedColumns = [],
  accountNumber,
  onClick,
  callback
}) => {
  const { t, i18n } = useTranslation("translations");
  const menuRef = useRef(null);
  const simpleExportContainerRef = useRef(null);
  const allExportContainerRef = useRef(null);
  const odooExportContainerRef = useRef(null);

  const simpleExportButtonRef = useRef(null);
  const allExportButtonRef = useRef(null);
  const odooExportButtonRef = useRef(null);

  useEffect(() => {
    const captureButton = (containerRef, buttonRef) => {
      if (containerRef?.current) {
        const button = containerRef.current.querySelector('.btn-custom');
        if (button) {
          buttonRef.current = button;
        }
      }
    };

    captureButton(simpleExportContainerRef, simpleExportButtonRef);
    captureButton(allExportContainerRef, allExportButtonRef);
    captureButton(odooExportContainerRef, odooExportButtonRef);
  });

  const allCols = useMemo(() => {
    if (!selectedColumns) return [];
    const newCols = columns
      .filter((col) => selectedColumns?.every((sCol) => sCol.field !== col?.field))
      .map((foundCol) => ({ ...foundCol, show: !!foundCol.persisted, newCol: true }));
    let currCols = [];
    selectedColumns.forEach((sCol) => {
      const currCol = columns.find((col) => col.field === sCol.field);
      if (currCol) currCols.push({ ...currCol, show: sCol.show });
    });
    return [...newCols, ...currCols];
  }, [selectedColumns, i18n.language]);

  const filteredColumns = useMemo(() => {
    let _filteredColumns = [];
    selectedColumns.forEach((persistedCol) => {
      const column = columns?.find((col) => col.field === persistedCol?.field);
      if (column && (persistedCol.show || column.persisted)) _filteredColumns.push(column);
    });
    return _filteredColumns;
  }, [selectedColumns, columns]);

  const odooColumns = useMemo(() => {
    return [
      {
        field: 'bankAccountNumber',
        header: "Bank Account Number",
        exportHeader: "Bank Account Number",
        body: (transaction) => transaction?.accountNumber || accountNumber || "",
        formattedExportValue: true,
      },
      {
        field: 'bookingDate',
        header: "Date",
        exportHeader: "Date",
        formattedExportValue: true,
        preventExcelFormat: true,
        body: (transaction) => {
          if (!transaction.bookingDate) return "";
          return moment(transaction.bookingDate).format('YYYY-MM-DD');
        },
      },
      {
        field: 'partner',
        header: "Partner",
        exportHeader: "Partner",
        body: (transaction) =>
          !transaction?.debtorId ? transaction?.debtorName : transaction?.creditorName,
        formattedExportValue: true,
      },
      {
        field: 'additionalInformation',
        header: "Label",
        exportHeader: "Label",
      },
      {
        field: 'reference',
        header: "Reference",
        exportHeader: "Reference",
        body: () => "",
        formattedExportValue: true,
      },
      {
        field: 'amount',
        header: "Amount",
        exportHeader: "Amount",
        body: (transaction) => {
          const amount = parseFloat(transaction.transactionAmount) || 0;
          return transaction.debtorId ? -amount : amount;
        },
        formattedExportValue: true,
      },
      {
        field: 'amountInCurrency',
        header: "Amount in Currency",
        exportHeader: "Amount in Currency",
        body: (transaction) => {
          const amount = parseFloat(transaction.transactionAmount) || 0;
          return transaction.debtorId ? -amount : amount;
        },
        formattedExportValue: true,
      },
      {
        field: 'accountCurrency',
        header: "Foreign Currency",
        exportHeader: "Foreign Currency",
      },
      {
        field: 'transactionType',
        header: "Transaction Type",
        exportHeader: "Transaction Type",
        body: (transaction) => transaction?.transactionDetails?.bankTransactionCode ?? "",
        formattedExportValue: true,
      },
      {
        field: 'accountOnlineLink',
        header: "Account Online Link",
        exportHeader: "Account Online Link",
        body: () => "",
        formattedExportValue: true,
      },
      {
        field: 'onlineAccount',
        header: "Online Account",
        exportHeader: "Online Account",
        body: () => "",
        formattedExportValue: true,
      },
      {
        field: 'onlineTransactionIdentifier',
        header: "Online Transaction Identifier",
        exportHeader: "Online Transaction Identifier",
        body: () => "",
        formattedExportValue: true,
      },
      {
        field: 'journalEntry',
        header: "Journal Entry",
        exportHeader: "Journal Entry",
        body: () => "",
        formattedExportValue: true,
      },
      {
        field: 'termsAndConditions',
        header: "Terms and Conditions",
        exportHeader: "Terms and Conditions",
        body: () => "",
        formattedExportValue: true,
      },
    ];
  }, [columns, accountNumber]);



  const exportColumns = useCallback(
    ({ type }) => {
      if (type === 'odoo') {
        return odooColumns.map((col) => ({
          ...col,
          field: col.field,
          header: col.header ?? func.camelToFlat(col.field),
        }));
      }

      if (type === 'all') {
        return allCols?.map((col) => ({
          ...col,
          field: col.field,
          header: col.header ?? func.camelToFlat(col.field),
        })) ?? [];
      }

      return filteredColumns?.map((col) => ({
        ...col,
        field: col.field,
        header: col.header ?? func.camelToFlat(col.field),
      })) ?? [];
    },
    [filteredColumns, allCols, odooColumns]
  );

  const getExportData = useCallback((dataArray) => {
    if (!dataArray || dataArray.length === 0) {
      return dataArray || [];
    }
    return dataArray;
  }, []);

  const triggerExport = useCallback((buttonRef) => {
    if (buttonRef?.current) {
      setTimeout(() => {
        const element = buttonRef.current;
        if (element && element.click) {
          element.click();
        }
      }, 50);
    }
  }, []);

  const exportMenuModel = [
    {
      label: t("common.exportSelected"),
      command: () => triggerExport(simpleExportButtonRef)
    },
    {
      label: t("common.exportAllColumns"),
      command: () => triggerExport(allExportButtonRef)
    },
    {
      label: t("common.exportOdoo"),
      command: () => triggerExport(odooExportButtonRef)
    },
  ];

  return (
    <>
      <Menu model={exportMenuModel} popup ref={menuRef} appendTo={document.body} />
      <TooltipOverlay title="Export CSV" placement="bottom">
        <div
          onClick={(e) => menuRef.current?.toggle(e)}
          className="prime-table-toolbar__icon-btn"
        >
          <FontAwesomeIcon icon={faFileExcel} />
        </div>
      </TooltipOverlay>

      {/* Hidden export components */}
      <div ref={simpleExportContainerRef} style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
        <ExportCsvPrime
          data={getExportData(data)}
          onClick={onClick}
          excludedColumns={excludedColumns}
          columns={exportColumns({ type: 'simple' })}
          callback={callback}
        />
      </div>

      <div ref={allExportContainerRef} style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
        <ExportCsvPrime
          label={t("common.exportAllColumns")}
          data={getExportData(data)}
          onClick={onClick}
          columns={exportColumns({ type: 'all' })}
          excludedColumns={excludedColumns}
          callback={callback}
        />
      </div>

      <div ref={odooExportContainerRef} style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
        <ExportCsvPrime
          label={t("common.exportOdoo")}
          data={getExportData(data)}
          onClick={onClick}
          columns={exportColumns({ type: 'odoo' })}
          excludedColumns={[]}
        />
      </div>
    </>
  );
};

export default BankJournalExportMenu;
